import { Check, Plus, Search, X } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/field'
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from '@/components/ui/overlays'
import { findSpace, spacesSync } from '@/data/spaces'
import type { SpaceKind } from '@/data/types'
import {
  DEFAULT_SPACE_KIND,
  SpaceKindTabs,
  filterByKind,
} from '@/features/spaces/SpaceKindTabs'
import { cn } from '@/lib/utils'

/**
 * The space slot is a fixed-width column at the head of the row, not a chip in
 * the action cluster.
 *
 * Two things fall out of that. The codes line up down the list, so filing is
 * scannable in one vertical pass instead of being read row by row; and an
 * unfiled meeting shows up as a GAP in that column, which is a quieter and more
 * useful signal than a dashed box competing with the controls on the right. The
 * full name lives in the tooltip — in a deal list the code is the name people
 * actually use.
 */
export function SpaceTag({
  spaceId,
  onChange,
  busy,
}: {
  spaceId: string | null
  onChange: (spaceId: string | null) => void
  busy?: boolean
}) {
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<SpaceKind>(DEFAULT_SPACE_KIND)
  // Resolved against every space, archived included: a meeting filed before the
  // deal closed still shows its code. Only the PICKER hides archived spaces.
  const space = findSpace(spaceId) ?? null

  const normalized = query.trim().toLowerCase()
  // A query spans BOTH kinds — see the same note in `filters.tsx`.
  const visible = (normalized ? spacesSync() : filterByKind(spacesSync(), kind)).filter(
    (item) =>
      !normalized ||
      item.name.toLowerCase().includes(normalized) ||
      item.code.toLowerCase().includes(normalized)
  )

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) return
        setQuery('')
        setKind(DEFAULT_SPACE_KIND)
      }}
    >
      {/* Span wrapper: Base UI's Tooltip.Trigger clones its child, and cloning
          the Popover.Trigger detaches it from the popover. */}
      <Tooltip content={space ? space.name : 'File in a space'}>
        <span className="inline-flex">
          <PopoverTrigger
            disabled={busy}
            aria-label={space ? `Space: ${space.name}. Change space` : 'File in a space'}
            className={cn(
              'grid h-5 w-10 shrink-0 place-items-center rounded-[var(--radius-xs)] border',
              'font-mono text-2xs tracking-tight outline-none transition-colors duration-100',
              'disabled:opacity-40',
              space
                ? 'border-[var(--color-border)] bg-[var(--color-surface-sunken)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
                : // Quiet at rest: a column of unfiled rows should read as gaps,
                  // not as a column of buttons shouting for attention.
                  'border-dashed border-[var(--color-border)] text-[var(--color-fg-subtle)]/50 hover:border-[var(--color-border-strong)] hover:text-[var(--color-fg-muted)]'
            )}
          >
            {space ? space.code : <Plus className="size-3" />}
          </PopoverTrigger>
        </span>
      </Tooltip>

      <PopoverContent align="start" className="w-64 p-0">
        <div className="relative border-b border-[var(--color-border)]">
          <Search className="absolute top-1/2 left-2.5 size-3 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search spaces…"
            aria-label="Search spaces"
            className="h-8 rounded-none border-0 pl-7 text-xs focus-visible:ring-0"
          />
        </div>

        <div className="border-b border-[var(--color-border)] px-2 py-2">
          <SpaceKindTabs value={kind} onChange={setKind} />
        </div>

        <ul className="max-h-64 overflow-y-auto p-1">
          {visible.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onChange(item.id === spaceId ? null : item.id)}
                className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left text-xs hover:bg-[var(--color-surface-hover)]"
              >
                <span className="w-9 shrink-0 font-mono text-2xs text-[var(--color-fg-subtle)]">
                  {item.code}
                </span>
                <span className="min-w-0 flex-1 truncate text-[var(--color-fg)]">{item.name}</span>
                {item.id === spaceId ? (
                  <Check className="size-3.5 shrink-0 text-[var(--color-primary)]" />
                ) : null}
              </button>
            </li>
          ))}
          {visible.length === 0 ? (
            <li className="px-2 py-6 text-center text-2xs text-[var(--color-fg-subtle)]">
              No spaces match.
            </li>
          ) : null}
        </ul>

        {space ? (
          <div className="border-t border-[var(--color-border)] p-1">
            <button
              type="button"
              onClick={() => onChange(null)}
              className="flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-left text-xs text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)]"
            >
              <X className="size-3.5" />
              Remove from {space.name}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
